import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { todayIso } from '../../utils/formatters.js';

/** Die 26 Kantone. Ein Tippfehler wäre sonst ein stiller Quellensteuertarif. */
const KANTONE = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR', 'JU', 'LU', 'NE',
  'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG', 'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH',
];

const EMPTY = {
  firstName: '', lastName: '', email: '', phone: '',
  hireDate: todayIso(), departmentId: '', positionId: '',
  baseSalary: '0', currency: 'EUR', managerId: '', annualLeaveDays: '',
  /* Für die BVG-Altersgutschriften (nach Alter gestaffelt, Art. 16 BVG) und
     für die Quellensteuer. Ohne Geburtsdatum rechnet das BVG mit einem festen
     Satz - gröber, aber nicht falsch. */
  birthDate: '',
  /* Der ARBEITSKANTON, nicht der Wohnkanton: quellenbesteuert wird dort, wo
     gearbeitet wird. Bei Wochenaufenthaltern und Grenzgängern fallen die
     beiden auseinander, und der Unterschied ist bares Geld. */
  workCanton: '',
  withholdingTaxLiable: false,
  withholdingTaxCode: '',
  childrenCount: '0',
};

export function EmployeeCreateModal({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const departments = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: () => hrApi.listDepartments().then((r) => r.items || []),
    enabled: open,
  });
  const positions = useQuery({
    queryKey: ['hr', 'positions', form.departmentId],
    queryFn: () =>
      hrApi.listPositions(form.departmentId ? { departmentId: form.departmentId } : undefined).then((r) => r.items || []),
    enabled: open,
  });
  /* Die möglichen Vorgesetzten sind schlicht alle aktiven Mitarbeitenden.
     Ohne diese Angabe hängt der ganze Freigabeweg in der Luft: die
     Ferienanträge und die Monatsabschlüsse gehen an die vorgesetzte Person,
     und wo keine hinterlegt ist, landen sie bei niemandem. */
  const mögliche = useQuery({
    queryKey: ['hr', 'employees', 'managers'],
    queryFn: () => hrApi.listEmployees({ limit: 200, status: 'ACTIVE' }).then((r) => r.items || []),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: (body) => hrApi.createEmployee(body),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
      setForm(EMPTY);
      setError(null);
      if (onCreated) {
        onCreated(created);
      }
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const handle = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  function submit(e) {
    e.preventDefault();
    setError(null);
    create.mutate({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      hireDate: form.hireDate,
      departmentId: form.departmentId || undefined,
      positionId: form.positionId || undefined,
      baseSalary: Number(form.baseSalary || 0),
      currency: form.currency || 'EUR',
      managerId: form.managerId || undefined,
      /* Leer heisst: der Standardwert der Ferienart gilt. Eine Null wäre
         etwas anderes - nämlich null Ferientage. */
      annualLeaveDays: form.annualLeaveDays.trim() === '' ? undefined : Number(form.annualLeaveDays),
      birthDate: form.birthDate || undefined,
      workCanton: form.workCanton || undefined,
      withholdingTaxLiable: form.withholdingTaxLiable,
      /* Nur mitschicken, wenn pflichtig. Ein Tarifcode ohne Pflicht wäre eine
         Angabe, die nie gilt - und beim nächsten Blick auf den Stamm eine
         Frage, die niemand beantworten kann. */
      withholdingTaxCode: form.withholdingTaxLiable ? (form.withholdingTaxCode.trim().toUpperCase() || undefined) : undefined,
      childrenCount: Number(form.childrenCount || 0),
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('hr.employees.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('hr.employees.modal.firstName')}</span>
            <input className="input" value={form.firstName} onChange={handle('firstName')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('hr.employees.modal.lastName')}</span>
            <input className="input" value={form.lastName} onChange={handle('lastName')} required />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('col.email')}</span>
            <input className="input" type="email" value={form.email} onChange={handle('email')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('hr.employees.modal.phone')}</span>
            <input className="input" value={form.phone} onChange={handle('phone')} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('hr.employees.modal.hireDate')}</span>
            <input className="input" type="date" value={form.hireDate} onChange={handle('hireDate')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('hr.employees.modal.department')}</span>
            <select className="select" value={form.departmentId} onChange={handle('departmentId')}>
              <option value="">{t('hr.employees.modal.optional')}</option>
              {(departments.data || []).map((d) => (
                <option key={d.id} value={d.id}>{d.code} - {d.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('hr.employees.modal.position')}</span>
            <select className="select" value={form.positionId} onChange={handle('positionId')}>
              <option value="">{t('hr.employees.modal.optional')}</option>
              {(positions.data || []).map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('hr.employees.modal.currency')}</span>
            <input className="input" maxLength={3} value={form.currency} onChange={handle('currency')} required />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('hr.employees.modal.manager')}</span>
          <select className="select" value={form.managerId} onChange={handle('managerId')}>
            <option value="">{t('hr.employees.modal.noManager')}</option>
            {(mögliche.data || []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </select>
          <span className="muted text-xs">{t('hr.employees.modal.managerHint')}</span>
        </label>
        <label className="field">
          <span className="field-label">{t('hr.employees.modal.annualLeave')}</span>
          <input
            className="input"
            type="number"
            min="0"
            max="365"
            step="0.5"
            value={form.annualLeaveDays}
            onChange={handle('annualLeaveDays')}
            placeholder="-"
          />
          <span className="muted text-xs">{t('hr.employees.modal.annualLeaveHint')}</span>
        </label>
        <label className="field">
          <span className="field-label">{t('hr.employees.modal.baseSalary')}</span>
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={form.baseSalary}
            onChange={handle('baseSalary')}
            required
          />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('hr.employees.modal.birthDate')}</span>
            <input className="input" type="date" value={form.birthDate} onChange={handle('birthDate')} />
            <span className="muted text-xs">{t('hr.employees.modal.birthDateHint')}</span>
          </label>
          <label className="field" style={{ maxWidth: 160 }}>
            <span className="field-label">{t('hr.employees.modal.children')}</span>
            <input
              className="input num"
              type="number"
              min="0"
              max="20"
              value={form.childrenCount}
              onChange={handle('childrenCount')}
            />
          </label>
        </div>

        <div className="field-row">
          <label className="field" style={{ maxWidth: 160 }}>
            <span className="field-label">{t('hr.employees.modal.workCanton')}</span>
            <select className="input" value={form.workCanton} onChange={handle('workCanton')}>
              <option value="">-</option>
              {KANTONE.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <span className="muted text-xs">{t('hr.employees.modal.workCantonHint')}</span>
          </label>
          <label className="field checkbox-field">
            <input
              type="checkbox"
              checked={form.withholdingTaxLiable}
              onChange={(e) => setForm((f) => ({ ...f, withholdingTaxLiable: e.target.checked }))}
            />
            <span>{t('hr.employees.modal.qstLiable')}</span>
          </label>
          {/* Der Tarifcode erscheint nur, wenn er gebraucht wird. Ein Feld,
              das nie gilt, ist eine Frage ohne Antwort. */}
          {form.withholdingTaxLiable && (
            <label className="field" style={{ maxWidth: 180 }}>
              <span className="field-label">{t('hr.employees.modal.qstCode')}</span>
              <input
                className="input mono"
                value={form.withholdingTaxCode}
                onChange={handle('withholdingTaxCode')}
                placeholder="A0N"
                maxLength={8}
                required
              />
              <span className="muted text-xs">{t('hr.employees.modal.qstCodeHint')}</span>
            </label>
          )}
        </div>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
